import React from 'react';
import {Box, Button, Typography} from '@mui/material';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';

const StatusBadge = ({status}) => {
    const map = {
        paid:      {background: 'rgba(22,101,52,0.12)',  color: '#166534'},
        pending:   {background: 'rgba(133,77,14,0.12)', color: '#854D0E'},
        cancelled: {background: 'var(--brand-bg, #F3F4F6)', color: 'var(--brand-text-muted, #6B7280)'},
    };
    const s = map[status?.toLowerCase()] || map.cancelled;
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center',
            px: 1.5, py: 0.4, borderRadius: '8px',
            background: s.background, color: s.color,
            fontSize: 13, fontWeight: 600, width: 'fit-content',
        }}>
            {status}
        </Box>
    );
};

const ColHeader = ({label, sortable}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
        <Typography sx={{fontSize: 12, fontWeight: 700, color: 'var(--brand-text-muted, #6B7280)', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
            {label}
        </Typography>
        {sortable && <UnfoldMoreIcon style={{fontSize: 14, color: 'var(--brand-text-muted, #9CA3AF)'}}/>}
    </Box>
);

const COLS = 'repeat(5, 1fr)';

const InvoiceHistoryTab = ({
    filteredInvoices,
    formatAmount,
    isCurrentMonthInvoice,
    handleViewInvoice,
    handleDownloadInvoice,
    handlePayNowClick,
}) => {

    return (
        <Box sx={{background: 'var(--brand-surface, #fff)', borderRadius: '16px', border: '1px solid var(--brand-border, #E5E7EB)', p: '28px 32px'}}>
            {/* Heading */}
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 3}}>
                <Typography sx={{fontSize: 20, fontWeight: 700, color: 'var(--brand-text, #111827)'}}>Billing history</Typography>
                <InfoOutlinedIcon style={{fontSize: 18, color: 'var(--brand-text-muted, #9CA3AF)'}}/>
            </Box>

            {/* Header row */}
            <Box sx={{display: 'grid', gridTemplateColumns: COLS, px: 2, py: 1.25, background: 'var(--brand-bg, #F9FAFB)', borderRadius: '10px', mb: 0.5}}>
                <ColHeader label="Invoice ID"/>
                <ColHeader label="Date" sortable/>
                <ColHeader label="Amount" sortable/>
                <ColHeader label="Status" sortable/>
                <Box sx={{display: 'flex', justifyContent: 'center'}}>
                    <ColHeader label="Actions"/>
                </Box>
            </Box>

            {/* Data rows */}
            {filteredInvoices.length === 0 ? (
                <Box sx={{py: 6, textAlign: 'center'}}>
                    <Typography sx={{color: 'var(--brand-text-muted, #9CA3AF)', fontSize: 15}}>No invoices found</Typography>
                </Box>
            ) : filteredInvoices.map((invoice, i) => (
                <Box key={i} sx={{
                    display: 'grid', gridTemplateColumns: COLS,
                    px: 2, py: 1.5, alignItems: 'center',
                    background: i % 2 !== 0 ? 'var(--brand-bg, #F9FAFB)' : 'var(--brand-surface, #fff)',
                    borderRadius: '8px',
                    '&:hover': {background: 'var(--brand-bg, #F3F4F6)'},
                }}>
                    <Box>
                        <Typography sx={{fontSize: 14, color: 'var(--brand-text, #374151)', fontWeight: 500}}>
                            #{invoice.invoice_number}
                        </Typography>
                        {invoice.invoice_type === 'dqt' && (
                            <Typography sx={{fontSize: 12, color: 'var(--brand-text-muted, #6B7280)'}}>
                                DQ Tokens
                            </Typography>
                        )}
                        {invoice.invoice_type === 'direct' && (
                            <Typography sx={{fontSize: 12, color: 'var(--brand-text-muted, #6B7280)'}}>
                                {invoice.units?.licences ? 'Licenses & credits' : 'Credits'}
                            </Typography>
                        )}
                    </Box>
                    <Typography sx={{fontSize: 14, color: 'var(--brand-text, #374151)'}}>
                        {invoice.month}
                    </Typography>
                    <Typography sx={{fontSize: 14, color: 'var(--brand-text, #374151)'}}>
                        ${formatAmount(invoice.total_amount)}
                    </Typography>
                    <StatusBadge status={invoice.status}/>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap', justifyContent: 'flex-end', width: '100%'}}>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleViewInvoice(invoice)}
                            sx={{fontSize: 12, fontWeight: 600, borderRadius: '6px', textTransform: 'none', borderColor: 'var(--brand-border, #E5E7EB)', color: 'var(--brand-text, #374151)', px: 1.5, py: 0.5, minWidth: 'unset', '&:hover': {borderColor: '#FF6900', color: '#FF6900', background: 'rgba(255,105,0,0.04)'}}}
                        >
                            View Invoice
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleDownloadInvoice(invoice)}
                            sx={{fontSize: 12, fontWeight: 600, borderRadius: '6px', textTransform: 'none', borderColor: 'var(--brand-border, #E5E7EB)', color: 'var(--brand-text, #374151)', px: 1.5, py: 0.5, minWidth: 'unset', '&:hover': {borderColor: '#FF6900', color: '#FF6900', background: 'rgba(255,105,0,0.04)'}}}
                        >
                            Download Invoice
                        </Button>
                        {invoice.status?.toLowerCase() === 'pending' && (
                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => handlePayNowClick(invoice)}
                                disabled={isCurrentMonthInvoice(invoice.billing_month)}
                                sx={{fontSize: 12, fontWeight: 600, borderRadius: '6px', textTransform: 'none', background: '#10B981', px: 1.5, py: 0.5, minWidth: 'unset', '&:hover': {background: '#059669'}, '&:disabled': {background: '#D1FAE5', color: '#6EE7B7'}}}
                            >
                                Pay Now
                            </Button>
                        )}
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

export default InvoiceHistoryTab;
